import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { debounce } from 'lodash';
import _ from 'lodash';
import { useTransformsStore } from '@/stores/transforms.store';
import { useRouter } from 'vue-router';
import { TRANSFORM_TYPE_ENUMS } from '@/utils/constants';

/**
 * Composable for managing topic pattern logic across transform components
 * Handles input/output topic operations, pattern validation, and data fetching
 */
export function useTopicPatternManager(props, emit, options = {}) {
    const transformsStore = useTransformsStore();
    const router = useRouter();

    // Extract SQL-specific options for handling inputTables
    const { inputPatterns = null, patternMainTable = null, patternLookupTable = null } = options;

    // Reactive state
    const pattern = ref('');
    const outputTopic = ref('');
    const inOutTopics = ref([]);

    // Loading states
    const loadingSetInOutTopics = ref(false);
    const topicTableDataLoading = ref(false);
    
    // Control silent mode for auto-save operations
    const silentMode = ref(false);

    // Error states
    const setInOutError = ref(null);
    const setInOutResult = ref('');
    const fetchInOutError = ref(null);

    // Internal change detection state - consolidated from transform files
    const lastSavedPattern = ref('');
    const lastSavedOutputTopic = ref('');
    const lastSavedInputPatterns = ref([]);
    const lastSavedPatternMainTable = ref('');
    const lastSavedPatternLookupTable = ref('');

    // Computed properties
    const isValidPattern = computed(() => {
        try {
            new RegExp(pattern.value);
            return true;
        } catch {
            return false;
        }
    });

    // Determine transform type for SQL-specific logic
    const transformType = computed(() => props.transform?.transform_type);
    const isSqlJoinType = computed(() => transformType.value === TRANSFORM_TYPE_ENUMS.sql_join);
    const isEnrichType = computed(() => transformType.value === TRANSFORM_TYPE_ENUMS.enrich);

    /**
     * Internal change detection - consolidated from all transform files
     */
    const havePatternsChanged = () => {
        // Get the expected default values for current transform type
        const currentDefaults = {
            pattern: props.transform?.config?.['transforms.input.topic.pattern'] || '',
            outputTopic: props.transform?.config?.['transforms.output.topic.pattern'] || ''
        };

        // If lastSaved values are still at initial defaults, compare against transform config defaults
        const baselinePattern = lastSavedPattern.value || currentDefaults.pattern;
        const baselineOutputTopic = lastSavedOutputTopic.value || currentDefaults.outputTopic;

        if (isSqlJoinType.value && inputPatterns) {
            return (
                pattern.value !== baselinePattern ||
                outputTopic.value !== baselineOutputTopic ||
                JSON.stringify(inputPatterns.value) !== JSON.stringify(lastSavedInputPatterns.value)
            );
        } else if (isEnrichType.value && patternMainTable && patternLookupTable) {
            return (
                pattern.value !== baselinePattern ||
                outputTopic.value !== baselineOutputTopic ||
                patternMainTable.value !== lastSavedPatternMainTable.value ||
                patternLookupTable.value !== lastSavedPatternLookupTable.value
            );
        } else {
            return pattern.value !== baselinePattern || outputTopic.value !== baselineOutputTopic;
        }
    };

    /**
     * Internal tracker update - consolidated from all transform files
     */
    const updateLastSavedPatterns = () => {
        lastSavedPattern.value = pattern.value;
        lastSavedOutputTopic.value = outputTopic.value;
        
        if (isSqlJoinType.value && inputPatterns) {
            lastSavedInputPatterns.value = [...inputPatterns.value];
        } else if (isEnrichType.value && patternMainTable && patternLookupTable) {
            lastSavedPatternMainTable.value = patternMainTable.value;
            lastSavedPatternLookupTable.value = patternLookupTable.value;
        }
    };

    /**
     * Initialize lastSaved values when patterns are loaded - consolidated from all transform files
     */
    const initializeChangeTracking = () => {
        const watchTargets = [pattern, outputTopic];
        if (inputPatterns) watchTargets.push(inputPatterns);
        if (patternMainTable) watchTargets.push(patternMainTable);
        if (patternLookupTable) watchTargets.push(patternLookupTable);

        // Single watcher for initialization only - doesn't trigger fetch
        watch(watchTargets, () => {
            // Only initialize if lastSaved values are still empty (first load)
            const isFirstLoad = !lastSavedPattern.value && !lastSavedOutputTopic.value;
            
            if (isFirstLoad && (pattern.value || outputTopic.value)) {
                updateLastSavedPatterns();
            }
        }, { immediate: true, deep: true, flush: 'sync' }); // Use sync flush to avoid race conditions
    };

    /**
     * Validates regex pattern and throws error if invalid
     */
    const validatePattern = () => {
        try {
            new RegExp(pattern.value);
        } catch (err) {
            throw new Error(`Invalid regex topic pattern: ${pattern.value}`);
        }
    };

    /**
     * Sets input/output topics for the transform (silent mode for auto-save)
     */
    const callSetInOutTopicsSilent = async () => {
        silentMode.value = true;
        try {
            return await callSetInOutTopics();
        } finally {
            silentMode.value = false;
        }
    };

    /**
     * Sets input/output topics for the transform
     * @param {boolean} showLoading - Whether to show loading UI states (default: true)
     */
    const callSetInOutTopics = async (showLoading = true) => {
        // Check if transform is available
        if (!props.transform?.id) {
            if (showLoading) setInOutError.value = 'Transform data not available';
            return;
        }

        // Validate input regex pattern
        try {
            validatePattern();
        } catch (err) {
            if (showLoading) setInOutError.value = err.message;
            return;
        }

        if (showLoading) {
            loadingSetInOutTopics.value = true;
            setInOutResult.value = '';
            setInOutError.value = null;
        }

        try {
            await transformsStore.setInputOutputTopic(props.transform.id, pattern.value, outputTopic.value);

            // Handle SQL-specific inputTables synchronization
            if (transformType.value === TRANSFORM_TYPE_ENUMS.sql_join && inputPatterns && props.implementationCurrentVersion?.implementation) {
                // Clone implementation to avoid mutating props
                const updatedImplementation = _.cloneDeep(props.implementationCurrentVersion);
                const inputTables = updatedImplementation.implementation.inputTables;

                if (inputTables) {
                    // Ensure inputTables has the right length to match user patterns
                    while (inputTables.length < inputPatterns.value.length) {
                        inputTables.push({
                            topicMatcherRegex: '',
                            createTableSQL: 'TBD',
                            name: `input_table_${inputTables.length + 1}`
                        });
                    }
                    
                    // Update inputTables with current user patterns
                    for (let i = 0; i < inputPatterns.value.length; i++) {
                        const pattern = inputPatterns.value[i];
                        if (pattern && pattern.trim() !== '') {
                            // Update existing table or ensure it exists
                            if (!inputTables[i]) {
                                inputTables[i] = {
                                    topicMatcherRegex: pattern,
                                    createTableSQL: 'TBD',
                                    name: `input_table_${i + 1}`
                                };
                            } else {
                                // Just update the pattern, preserve existing SQL and name
                                inputTables[i].topicMatcherRegex = pattern;
                            }
                        }
                    }
                    
                    // Remove extra tables if user removed patterns
                    if (inputTables.length > inputPatterns.value.length) {
                        inputTables.length = inputPatterns.value.length;
                    }

                    // Save the updated implementation to backend
                    await transformsStore.editImplementationDetails(props.transform.id, updatedImplementation);
                    // Don't emit 'implementation-updated' since we already saved - just update local state
                    emit('implementation-synced', updatedImplementation);
                }
            } else if (
                transformType.value === TRANSFORM_TYPE_ENUMS.enrich &&
                patternMainTable &&
                patternLookupTable &&
                props.implementationCurrentVersion?.implementation
            ) {
                // Clone implementation to avoid mutating props
                const updatedImplementation = _.cloneDeep(props.implementationCurrentVersion);

                // Update main and lookup table patterns for enrich transforms
                if (updatedImplementation.implementation.mainTable) {
                    updatedImplementation.implementation.mainTable.topicMatcherRegex = patternMainTable.value;
                }
                if (updatedImplementation.implementation.lookupTable) {
                    updatedImplementation.implementation.lookupTable.topicMatcherRegex = patternLookupTable.value;
                }

                // Save the updated implementation to backend
                await transformsStore.editImplementationDetails(props.transform.id, updatedImplementation);
                // Don't emit 'implementation-updated' since we already saved - just update local state
                emit('implementation-synced', updatedImplementation);
            }

            if (showLoading) {
                setInOutResult.value = 'Successfully set input/output topics';
            }
            // Update tracking after successful save
            updateLastSavedPatterns();
            emit('implementation-saved');
        } catch (e) {
            console.error(`${showLoading ? 'Error' : 'Silent auto-save error'} setting input/output topics`, e);
            if (showLoading) {
                setInOutResult.value = '';
                setInOutError.value = e;
                emit('error', e);
            }
        } finally {
            if (showLoading) {
                loadingSetInOutTopics.value = false;
            }
        }
    };

    /**
     * Debounced pattern save for auto-save functionality - consolidated internal logic
     */
    const debouncedPatternSave = debounce(async () => {
        const hasPatterns = pattern.value.trim() || outputTopic.value.trim() || 
            (isEnrichType.value && (patternMainTable?.value?.trim() || patternLookupTable?.value?.trim()));
        const patternsChanged = havePatternsChanged();
        
        if (!loadingSetInOutTopics.value && hasPatterns && patternsChanged) {
            try {
                await callSetInOutTopics(false); // Silent mode - no loading UI
                // updateLastSavedPatterns() is called in callSetInOutTopics on success
            } catch (error) {
                console.error('Auto-save topic patterns failed:', error);
            }
        }
    }, 3000);

    /**
     * Fetches topics matching the current pattern
     * @param {boolean} silent - If true, won't show loading skeleton (for auto-save operations)
     */
    const fetchInOutTopics = async (silent = false) => {
        // Check if transform is available
        if (!props.transform?.id) {
            fetchInOutError.value = 'Transform data not available';
            inOutTopics.value = [];
            return;
        }

        // Validate input regex before querying backend
        try {
            validatePattern();
        } catch (err) {
            fetchInOutError.value = err.message;
            inOutTopics.value = [];
            return;
        }

        if (!silent) {
            topicTableDataLoading.value = true;
        }
        fetchInOutError.value = null;

        try {
            const res = await transformsStore.topicsForPattern(pattern.value, outputTopic.value);

            const inOutTopicsRes = [];
            const outputTopicPrefix = `transform_${props.transform.id}_0`;
            let idx = 0;

            for (const in_out_topic of res) {
                if (idx >= 20) break;
                idx++;
                inOutTopicsRes.push([in_out_topic[0], `${outputTopicPrefix}.${in_out_topic[1]}`]);
            }

            inOutTopics.value = inOutTopicsRes;
            return res;
        } catch (e) {
            console.error('Error fetching topics for pattern', e);
            fetchInOutError.value = e;
            emit('error', e);
        } finally {
            if (!silent) {
                topicTableDataLoading.value = false;
            }
        }
    };

    /**
     * Initializes pattern and output topic from props
     * Only updates if values actually changed to prevent unnecessary refetches
     */
    const initializePatternData = () => {
        const newPattern = props.transform?.config?.['transforms.input.topic.pattern'] || '';
        const newOutputTopic = props.transform?.config?.['transforms.output.topic.pattern'] || '';

        // Only update if values actually changed to prevent unnecessary refetches
        if (pattern.value !== newPattern) {
            pattern.value = newPattern;
        }
        if (outputTopic.value !== newOutputTopic) {
            outputTopic.value = newOutputTopic;
        }
    };

    // Watch pattern and outputTopic changes with debounced fetch
    const debouncedFetch = debounce(() => {
        if (pattern.value && outputTopic.value) {
            fetchInOutTopics(silentMode.value);
        }
    }, 500);

    watch([pattern, outputTopic], debouncedFetch);

    // Cancel debounced functions on unmount to prevent memory leaks
    onBeforeUnmount(() => {
        debouncedFetch.cancel();
        debouncedPatternSave.cancel();
    });

    // Helper function to navigate to topic
    const navigateToTopic = (topicName) => {
        const url = router.resolve({ name: 'topic-details', params: { topic_id: topicName } });
        window.open(url.href, '_blank');
    };

    /**
     * Creates a clean content change handler with integrated auto-save
     * All pattern change detection and saving logic is handled internally
     * @param {Function} originalHandler - The original handleContentChange function
     * @param {Function} tabValidator - Function that determines if tab should trigger auto-save
     * @returns {Function} Enhanced handler with auto-save functionality
     */
    const createContentChangeHandler = (originalHandler, tabValidator) => {
        return (tabId, content) => {
            // Call original handler first (saves Monaco content immediately)
            originalHandler(tabId, content);

            // Check if we have any patterns (including enrich-specific ones)
            const hasAnyPatterns = pattern.value.trim() || outputTopic.value.trim() || 
                (isEnrichType.value && (patternMainTable?.value?.trim() || patternLookupTable?.value?.trim()));

            // Auto-save topic patterns when code changes that might affect topic matching
            const shouldAutoSaveTopicPatterns =
                tabValidator(tabId) &&
                hasAnyPatterns &&
                !loadingSetInOutTopics.value;

            if (shouldAutoSaveTopicPatterns) {
                // Trigger debounced pattern save (all logic is internal now)
                debouncedPatternSave();
            }
        };
    };

    // Initialize change tracking
    initializeChangeTracking();

    return {
        // Reactive state
        pattern,
        outputTopic,
        inOutTopics,

        // Loading states
        loadingSetInOutTopics,
        topicTableDataLoading,

        // Error states
        setInOutError,
        setInOutResult,
        fetchInOutError,

        // Computed
        isValidPattern,

        // Methods
        callSetInOutTopics,
        callSetInOutTopicsSilent,
        fetchInOutTopics,
        initializePatternData,
        navigateToTopic,
        validatePattern,

        // Auto-save functions
        debouncedPatternSave,
        createContentChangeHandler
    };
}
